import apiClient from '../../../shared/api/apiClient';
import type {
  DashboardStatsDto,
  AdminHealthDto,
  AdminBulkResponse,
  AdminLicenseBulkAction,
  AdminLicenseCreateRequest,
  AdminLicenseDetailDto,
  AdminLicenseDto,
  AdminLicensePatchRequest,
  AdminUserBulkAction,
  AdminUserCreateRequest,
  AdminUserDto,
  AdminUserPatchRequest,
  AdminCompanyDto,
  AdminCompanyBulkAction,
  AdminCompanyCreateRequest,
  AdminCompanyDeleteOptions,
  AdminCompanyDetailDto,
  AdminCompanyPatchRequest,
  AdminTicketDto,
  AdminTicketListQuery,
  AdminTicketListResponse,
  AdminTicketMessageRequest,
  AdminTicketPatchRequest,
  AdminPageDto,
} from '../types';
import { isWithinAdminUtcDays } from '../lib/adminDates';

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

type CollectionPayload<T> = T[] | AdminPageDto<T>;

const SixtyDaysMs = 60 * 24 * 60 * 60 * 1000;

const getItems = <T>(payload: CollectionPayload<T>): T[] =>
  Array.isArray(payload) ? payload : payload.items;

const isActiveExpiringLicense = (license: AdminLicenseDto, now: Date) =>
  license.status === 'active'
  && license.dueTime !== null
  && isWithinAdminUtcDays(license.dueTime, 60, now);

export const superAdminApi = {
  login: async (username: string, password: string): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/login', {
      username,
      password,
    });
    return response.data;
  },

  getDashboard: async (): Promise<DashboardStatsDto> => {
    const response = await apiClient.get<DashboardStatsDto>('/super-admin/dashboard');
    return response.data;
  },

  getHealth: async (): Promise<AdminHealthDto> => {
    const response = await apiClient.get<AdminHealthDto>('/super-admin/health');
    return response.data;
  },

  getExpiringLicenses: async (now = new Date()): Promise<AdminLicenseDto[]> => {
    const dueBefore = new Date(now.getTime() + SixtyDaysMs);
    const response = await apiClient.get<CollectionPayload<AdminLicenseDto>>(
      '/super-admin/licenses',
      {
        params: {
          status: 'active',
          dueBefore: dueBefore.toISOString(),
          sort: 'dueTime',
        },
      }
    );

    return getItems(response.data)
      .filter((license) => isActiveExpiringLicense(license, now))
      .sort((left, right) =>
        (left.dueTime ?? '').localeCompare(right.dueTime ?? '')
      );
  },

  getLatestTickets: async (): Promise<AdminTicketDto[]> => {
    const response = await apiClient.get<CollectionPayload<AdminTicketDto>>(
      '/super-admin/tickets',
      { params: { sort: '-updatedAt', limit: 5 } }
    );

    return [...getItems(response.data)]
      .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))
      .slice(0, 5);
  },

  listTickets: async (
    query: AdminTicketListQuery,
    signal?: AbortSignal
  ): Promise<AdminTicketListResponse> => {
    const config = {
      params: {
        ...(query.status ? { status: query.status } : {}),
        ...(query.category ? { category: query.category } : {}),
        ...(query.q?.trim() ? { q: query.q.trim() } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
        limit: query.limit ?? 30,
      },
      signal,
    } as Parameters<typeof apiClient.get>[1];
    const response = await apiClient.get<AdminTicketListResponse>(
      '/super-admin/tickets',
      config
    );
    return response.data;
  },

  getTicket: async (id: number): Promise<AdminTicketDto> => {
    const response = await apiClient.get<AdminTicketDto>(`/super-admin/tickets/${id}`);
    return response.data;
  },

  patchTicket: async (
    id: number,
    patch: AdminTicketPatchRequest
  ): Promise<AdminTicketDto> => {
    const response = await apiClient.patch<AdminTicketDto>(
      `/super-admin/tickets/${id}`,
      patch
    );
    return response.data;
  },

  addTicketMessage: async (
    id: number,
    request: AdminTicketMessageRequest
  ): Promise<AdminTicketDto> => {
    const response = await apiClient.post<AdminTicketDto>(
      `/super-admin/tickets/${id}/messages`,
      request
    );
    return response.data;
  },

  getLicenses: async (): Promise<AdminLicenseDto[]> => {
    const response = await apiClient.get<CollectionPayload<AdminLicenseDto>>(
      '/super-admin/licenses'
    );
    return getItems(response.data);
  },

  getLicense: async (id: number): Promise<AdminLicenseDetailDto> => {
    const response = await apiClient.get<AdminLicenseDetailDto>(
      `/super-admin/licenses/${id}`
    );
    return response.data;
  },

  createLicense: async (
    data: AdminLicenseCreateRequest
  ): Promise<AdminLicenseDetailDto> => {
    const response = await apiClient.post<AdminLicenseDetailDto>(
      '/super-admin/licenses',
      data
    );
    return response.data;
  },

  updateLicense: async (
    id: number,
    data: AdminLicensePatchRequest
  ): Promise<AdminLicenseDetailDto> => {
    const response = await apiClient.patch<AdminLicenseDetailDto>(
      `/super-admin/licenses/${id}`,
      data
    );
    return response.data;
  },

  revokeLicenseMachine: async (id: number, machineId: number): Promise<void> => {
    await apiClient.delete(`/super-admin/licenses/${id}/machines/${machineId}`);
  },

  extendLicense: async (id: number, months: number): Promise<AdminLicenseDetailDto> => {
    const response = await apiClient.post<AdminLicenseDetailDto>(
      `/super-admin/licenses/${id}/extend`,
      { months }
    );
    return response.data;
  },

  deleteLicense: async (id: number): Promise<void> => {
    await apiClient.delete(`/super-admin/licenses/${id}`);
  },

  bulkLicenses: async (
    action: AdminLicenseBulkAction,
    ids: number[],
    months?: number
  ): Promise<AdminBulkResponse> => {
    const response = await apiClient.post<AdminBulkResponse>(
      '/super-admin/licenses/bulk',
      {
        action,
        ids,
        ...(action === 'extend' ? { params: { months: months ?? 12 } } : {}),
      }
    );
    return response.data;
  },

  getUsers: async (): Promise<AdminUserDto[]> => {
    const response = await apiClient.get<CollectionPayload<AdminUserDto>>(
      '/super-admin/users'
    );
    return getItems(response.data);
  },

  getUser: async (id: number): Promise<AdminUserDto> => {
    const response = await apiClient.get<AdminUserDto>(`/super-admin/users/${id}`);
    return response.data;
  },

  createUser: async (data: AdminUserCreateRequest): Promise<AdminUserDto> => {
    const response = await apiClient.post<AdminUserDto>('/super-admin/users', data);
    return response.data;
  },

  updateUser: async (
    id: number,
    data: AdminUserPatchRequest
  ): Promise<AdminUserDto> => {
    const response = await apiClient.patch<AdminUserDto>(
      `/super-admin/users/${id}`,
      data
    );
    return response.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await apiClient.delete(`/super-admin/users/${id}`);
  },

  resetUserPassword: async (id: number): Promise<void> => {
    await apiClient.post(`/super-admin/users/${id}/reset-password`);
  },

  bulkUsers: async (
    action: AdminUserBulkAction,
    ids: number[]
  ): Promise<AdminBulkResponse> => {
    const response = await apiClient.post<AdminBulkResponse>(
      '/super-admin/users/bulk',
      { action, ids }
    );
    return response.data;
  },

  getCompanies: async (): Promise<AdminCompanyDto[]> => {
    const response = await apiClient.get<CollectionPayload<AdminCompanyDto>>(
      '/super-admin/companies'
    );
    return getItems(response.data);
  },

  getCompany: async (id: number): Promise<AdminCompanyDetailDto> => {
    const response = await apiClient.get<AdminCompanyDetailDto>(
      `/super-admin/companies/${id}`
    );
    return response.data;
  },

  createCompany: async (
    data: AdminCompanyCreateRequest
  ): Promise<AdminCompanyDetailDto> => {
    const response = await apiClient.post<AdminCompanyDetailDto>(
      '/super-admin/companies',
      data
    );
    return response.data;
  },

  updateCompany: async (
    id: number,
    data: AdminCompanyPatchRequest
  ): Promise<AdminCompanyDetailDto> => {
    const response = await apiClient.patch<AdminCompanyDetailDto>(
      `/super-admin/companies/${id}`,
      data
    );
    return response.data;
  },

  deleteCompany: async (id: number, options: AdminCompanyDeleteOptions): Promise<void> => {
    await apiClient.delete(`/super-admin/companies/${id}`, {
      headers: options.confirmCascade ? { 'X-Confirm-Cascade': 'true' } : undefined,
      params: options.expectedCounts,
    });
  },

  bulkCompanies: async (
    action: AdminCompanyBulkAction,
    ids: number[],
    expectedCounts?: Record<number, { users: number; licenses: number }>
  ): Promise<AdminBulkResponse> => {
    const response = action === 'delete'
      ? await apiClient.post<AdminBulkResponse>(
        '/super-admin/companies/bulk',
        { action, ids, expectedCounts: expectedCounts ?? {} },
        { headers: { 'X-Confirm-Cascade': 'true' } }
      )
      : await apiClient.post<AdminBulkResponse>('/super-admin/companies/bulk', { action, ids });
    return response.data;
  },

};
